/* eslint-disable max-lines */
import React, {FC, useRef, useState} from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {Loader} from '@optimaxdev/design-system';
import type {OnApproveData} from '@paypal/paypal-js';
import {PayPalButtons, PayPalScriptProvider} from '@paypal/react-paypal-js';
import classNames from 'classnames';
import {connect} from 'react-redux';

import {useFeature} from 'components/feature';
import {CHECKOUT_ERRORS, CHECKOUT_PAYMENT_STEP} from 'constants/analytics/events/checkout';
import {PAYPAL_PURCHASE_STORAGE} from 'constants/session';

import {usePayPalButtonHandlers} from 'desktop/hooks/payPal/usePayPalButtonHandlers';
import {PaymentStepAgreement} from 'desktop/pages/checkout/components/paymentStepAgreement/paymentStepAgreement';
import {ApplicationStoreType} from 'desktop/store';
import {OrderReviewTotalsType} from 'desktop/types/orderReviewTotalsType';
import {handlePayPalDesktopErrorAnalytic} from 'features/expressPayments/payPalV3/utils/analytics';
import {getTokenPayPal} from 'features/expressPayments/payPalV3/utils/getTokenPayPal';
import {callbackWithAnalyticsEvent} from 'libs/analytics/callbackWithAnalytic';
import {logWithSentry} from 'libs/sentry/sentry';
import {sessionStore} from 'libs/storage/storage';
import {PaymentType} from 'types/PaymentMethodsType';
import {ShippingMethodType} from 'types/shippingMethodsType';

import {PAYMENTS} from '../../constants/paymentStep';
import {useShippingProtectionEnabled} from '../../hooks/shippingProtection/useShippingProtectionEnabled';
import {useGetAddressForPayPal} from '../../hooks/useGetShippingAddressForPayPal/useGetShippingAddressForPayPal';
import {setOptionPaymentMethod} from '../../store/reducers/analytics/analytics';
import {resaveDelivery as resaveDeliveryMethod} from '../../store/reducers/checkout/checkout';
import {savePaymentMethod} from '../../store/reducers/paymentStep/paymentMethodSave/paymentMethodSave';
import {getOrderReviewTotals} from '../../store/selectors/orderReviewTotals/orderReviewTotals';
import {
    getPayPalV3MessageError,
    getPaymentMethodSaveLoading,
} from '../../store/selectors/paymentStep/paymentMethodSave/paymentMethodSave';
import {
    getIsSelectedByKey,
    getPaymentMethodByKey,
} from '../../store/selectors/paymentStep/paymentStep';
import {
    getSelectedShippingMethodCode,
    getShippingMethodList,
} from '../../store/selectors/shippingMethods/shippingMethods';
import {PayPalIcon} from '../icons/checkoutIcons';
import {PaymentWrapperConnected} from '../paymentWrapper/paymentWrapper';

import {PaypalButtonFooter} from '../paypalButtonFooter/paypalButtonFooter';

import style from './buttonStyle';

import s from './paymentPayPalV3.scss';

type PaymentPayPalV3Type = {
    method: PaymentType;
    isSelected: boolean;
    totals: OrderReviewTotalsType;
    errorMessage: string | boolean;
    isLoading: boolean;
    resaveDelivery: () => void;
    setOptionMethod: () => void;
    save: ({method, token}: {token: string; method: string; id: string}) => void;
    shippingMethodsList: Array<ShippingMethodType>;
    selectedShippingMethodCode: string;
};

/**
 * PaymentPayPalV3
 */
const PaymentPayPalV3: FC<PaymentPayPalV3Type> = ({
    errorMessage,
    isLoading,
    isSelected,
    method: {code, rest_api: {client_id: clientId} = {}},
    resaveDelivery,
    save,
    selectedShippingMethodCode,
    setOptionMethod,
    shippingMethodsList,
    totals,
}) => {
    const isFacelift = useFeature('dCheckoutFacelift');

    const sessionIdRef = useRef('');
    const paypalButtonHandlers = usePayPalButtonHandlers(sessionIdRef, 'checkout');
    const [isLoaded, setIsLoaded] = useState<boolean>(false);
    const shippingAddress = useGetAddressForPayPal();
    const {isEnabled, price} = useShippingProtectionEnabled();

    const onApprove = async (data: OnApproveData) => {
        if (!sessionIdRef.current) return;
        await callbackWithAnalyticsEvent('place order', 'PayPal', async () => {
            save({
                id: sessionIdRef.current,
                method: PAYMENTS.METHOD_PAYPAL_V3,
                token: data.orderID,
            });
        });
    };

    const createOrder = async () => {
        const {id, token} = await getTokenPayPal({
            isEnabledShippingProtection: isEnabled,
            price,
            selectedShippingMethodCode,
            shippingAddress,
            shippingMethodsList,
            totals,
        });
        if (!token || !id) {
            sendDesktopAnalyticsEvent(CHECKOUT_ERRORS, {
                action: `PayPal`,
                message: 'Initialization error',
                step: 'Payments',
            });
            logWithSentry(new Error('PaypalV3: initialization'), 'PaypalV3: Error in SDK');
            return '';
        }
        sessionIdRef.current = id;
        return token;
    };

    if (!code || !clientId) return null;

    return (
        <PaymentWrapperConnected code={code} isSelected={isSelected} title={<PayPalIcon />}>
            {!isLoaded && <Loader className={s.initLoader} />}
            <p className={classNames(s.text, s.margin, {[s.margin]: isFacelift})}>
                {`Click the button to be redirected to ${isFacelift ? 'PayPal' : 'PayPal.com'} & complete your purchase.`}
                <PaymentStepAgreement
                    className={classNames(s.text, {[s.textFacelift]: isFacelift})}
                />
            </p>
            <div
                className={classNames({
                    [s.hide]: !isLoaded,
                })}
                data-exp="dPayPalV3Button"
            >
                <PayPalScriptProvider options={{clientId, components: 'buttons'}}>
                    <PayPalButtons
                        onClick={() => {
                            sendDesktopAnalyticsEvent(CHECKOUT_PAYMENT_STEP, {
                                message: 'CTA - PayPal',
                                method: 'Payment',
                            });
                            sessionStore.set(PAYPAL_PURCHASE_STORAGE, 'Checkout');
                            setOptionMethod();
                        }}
                        onError={(error: Record<string, unknown>) =>
                            handlePayPalDesktopErrorAnalytic(error, true)
                        }
                        className={s.button}
                        createOrder={createOrder}
                        onApprove={onApprove}
                        onCancel={resaveDelivery}
                        onInit={() => setIsLoaded(true)}
                        {...paypalButtonHandlers}
                        style={style}
                    />
                </PayPalScriptProvider>
            </div>
            {isLoaded && <PaypalButtonFooter error={String(errorMessage)} loading={isLoading} />}
        </PaymentWrapperConnected>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType) => ({
    errorMessage: getPayPalV3MessageError(store),
    isLoading: getPaymentMethodSaveLoading(store),
    isSelected: getIsSelectedByKey(store, PAYMENTS.METHOD_PAYPAL_V3),
    method: getPaymentMethodByKey(store, PAYMENTS.METHOD_PAYPAL_V3),
    selectedShippingMethodCode: getSelectedShippingMethodCode(store),
    shippingMethodsList: getShippingMethodList(store),
    totals: getOrderReviewTotals(store),
});

const mapDispatchToProps = {
    resaveDelivery: resaveDeliveryMethod,
    save: savePaymentMethod,
    setOptionMethod: setOptionPaymentMethod,
};

export const PaymentPaypalV3Connected = connect(
    mapStateToProps,
    mapDispatchToProps,
)(PaymentPayPalV3);
